import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Overlay, Popover, Row } from "react-bootstrap";
import Moment from "react-moment";
import { connect } from "react-redux";
import { Formik, Form } from "formik";
import { Link } from "react-router-dom";
import axios from "../../../apis/Api";
import storage from "../../../apis/Storage";
import { VzPageContainer } from "../../../layouts/VzPageContainer";
import SCRV2TransactionReversalDataTable from "./SCRV2TransactionReversalDataTable";
import SCRV2TransactionReversalDetailModal from "./SCRV2TransactionReversalDetailModal";
import FormikControl from "../../../layouts/Input/FormikControl";
import {
  formatDate,
  renderCell,
  renderHeader,
  toCommaSeparated,
} from "../../../utilities/FUNCTIONS";
import {
  GET_DISTRIBUTOR_DDL,
  GET_ADDJOB_DISTRIBUTIONCENTER_DDL,
} from "../../../Types";
import { getDropdownList, getDropdownListByPost } from "../../Shared/dropdownListActions";
import {
  getSCRReversibleTransactionList,
  getSCRTransactionDetail,
  peekSCRTransactionDetailCache,
  prefetchSCRTransactionDetail,
  reopenSCRTransaction,
} from "./SCRV2TransactionReversalActions";
import { ReversalStatusBadge } from "./SCRV2ReversalStatusHelper";

const MAX_DATE_RANGE_MONTHS = 3;

const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

const startOfDay = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getPreviousBusinessDate = () => {
  const date = startOfDay(new Date());
  date.setDate(date.getDate() - 1);
  return date;
};

const getMaxToDate = (fromDate) =>
  fromDate ? addMonths(startOfDay(fromDate), MAX_DATE_RANGE_MONTHS) : null;

const getMinFromDate = (toDate) =>
  toDate ? addMonths(startOfDay(toDate), -MAX_DATE_RANGE_MONTHS) : null;

const getMaxFromDate = (toDate) => {
  const latestAllowed = getPreviousBusinessDate();
  if (!toDate) {
    return latestAllowed;
  }

  const normalizedToDate = startOfDay(toDate);
  return normalizedToDate < latestAllowed ? normalizedToDate : latestAllowed;
};

const getMaxSelectableToDate = (fromDate = null) => {
  const latestAllowed = getPreviousBusinessDate();
  const rangeMax = fromDate ? getMaxToDate(fromDate) : null;

  if (!rangeMax || rangeMax > latestAllowed) {
    return latestAllowed;
  }

  return rangeMax;
};

const normalizeDateRange = (fromDate, toDate, changedField = null) => {
  let from = fromDate ? new Date(fromDate) : null;
  let to = toDate ? new Date(toDate) : null;

  if (!from || !to) {
    return { fromDate: from || fromDate, toDate: to || toDate };
  }

  if (from > to) {
    if (changedField === "fromDate") {
      to = new Date(from);
    } else if (changedField === "toDate") {
      from = new Date(to);
    } else {
      to = new Date(from);
    }
  }

  const maxTo = addMonths(from, MAX_DATE_RANGE_MONTHS);
  if (to > maxTo) {
    if (changedField === "toDate") {
      from = addMonths(to, -MAX_DATE_RANGE_MONTHS);
    } else {
      to = maxTo;
    }
  }

  const maxSelectableTo = getPreviousBusinessDate();
  if (to > maxSelectableTo) {
    to = maxSelectableTo;
    if (from > to) {
      from = addMonths(to, -MAX_DATE_RANGE_MONTHS);
    }
  }

  return { fromDate: startOfDay(from), toDate: startOfDay(to) };
};

const createDefaultDates = () => {
  const toDate = getPreviousBusinessDate();
  const fromDate = new Date(toDate);
  return { fromDate, toDate };
};

const createDefaultFilter = () => {
  const { fromDate, toDate } = createDefaultDates();

  return {
    distributor: [],
    distCenter: [],
    deliveryManName: [],
    paymentType: [],
    shopCode: "",
    invoiceNo: "",
    scrNo: "",
    fromDate,
    toDate,
  };
};

const SCRV2TransactionReversal = (props) => {
  const userRights = useMemo(() => JSON.parse(storage.get("_userRights")), []);
  const [currentPageNo, setCurrentPageNo] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const ref = useRef(null);
  const openDetailRequestRef = useRef(0);
  const paymentTypesCacheRef = useRef(new Map());
  const deliveryUsersCacheRef = useRef(new Map());
  const [filter, setFilter] = useState(createDefaultFilter);
  const [distributorList, setDistributorList] = useState([]);
  const [distCenterList, setDistCenterList] = useState([]);
  const [deliveryManNameList, setDeliveryManNameList] = useState([]);
  const [paymentTypeList, setPaymentTypeList] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reopening, setReopening] = useState(false);

  const reportList = props.reportList || {};

  const formRequestObject = (overrides = {}) => {
    const current = { ...filter, ...overrides };

    return {
      fromDate: current.fromDate ? formatDate(current.fromDate) : null,
      toDate: current.toDate ? formatDate(current.toDate) : null,
      distributorIds: toCommaSeparated(current.distributor),
      distCenterIds: toCommaSeparated(current.distCenter),
      dlmIds: toCommaSeparated(current.deliveryManName),
      paymentTypeIds: toCommaSeparated(current.paymentType),
      shopCode: current.shopCode?.trim() || null,
      invoiceNo: current.invoiceNo?.trim() || null,
      scrNo: current.scrNo?.trim() || null,
      pageNumber:
        overrides.pageNumber !== undefined
          ? overrides.pageNumber
          : currentPageNo + 1,
      pageSize:
        overrides.pageSize !== undefined ? overrides.pageSize : currentPageSize,
      searchText:
        overrides.searchText !== undefined ? overrides.searchText : searchText,
    };
  };

  const fetchReportData = (request = null, options = {}) => {
    props.getSCRReversibleTransactionList(request || formRequestObject(), options);
  };

  const searchChanged = (text) => {
    setCurrentPageNo(0);
    setSearchText(text);
    fetchReportData(formRequestObject({ pageNumber: 1, searchText: text }));
  };

  useEffect(() => {
    fetchReportData(formRequestObject({ pageNumber: 1 }));
    props.getDropdownList(`api/Distributor/GetDistributorsDDL`, GET_DISTRIBUTOR_DDL);
  }, []);

  useEffect(() => {
    if (userRights != null && userRights?.userType === "DistributorUser") {
      const scopedDistributors = (props.allDistributorList || []).filter(
        (x) => x.value === userRights?.distributorId
      );
      if (scopedDistributors?.length > 0) {
        setFilter((prev) => ({
          ...prev,
          distributor: scopedDistributors,
        }));
      }
      setDistributorList(scopedDistributors);
    } else if (userRights != null && userRights?.userType === "CoreAppUser") {
      const scopedDistributors = (props.allDistributorList || []).filter((x) =>
        userRights?.distributorId?.includes(x.value)
      );
      setDistributorList(scopedDistributors);
    } else {
      setDistributorList(props.allDistributorList || []);
    }
  }, [props.allDistributorList]);

  useEffect(() => {
    if (userRights != null && userRights?.userType === "DistributorUser") {
      const scopedCenters = (props.selectedAddJobDistCentersList || []).filter((x) =>
        userRights?.distCenterIds?.includes(x.value)
      );
      setDistCenterList(scopedCenters);
    } else {
      setDistCenterList(props.selectedAddJobDistCentersList || []);
    }
  }, [props.selectedAddJobDistCentersList]);

  useEffect(() => {
    if (filter.distributor?.length > 0) {
      props.getDropdownListByPost(
        `api/DistCenter/GetDistCentersDDL`,
        { distributorIds: toCommaSeparated(filter.distributor) },
        GET_ADDJOB_DISTRIBUTIONCENTER_DDL
      );
      loadPaymentTypes(toCommaSeparated(filter.distributor));
    } else if (userRights?.userType !== "DistributorUser") {
      setDistCenterList([]);
      setPaymentTypeList([]);
    }
  }, [filter.distributor]);

  useEffect(() => {
    if (filter.distCenter?.length > 0) {
      getCenterDeliveryUsers(toCommaSeparated(filter.distCenter));
    } else {
      setDeliveryManNameList([]);
    }
  }, [filter.distCenter]);

  const openFilterModal = (event) => {
    setShow(!show);
    setTarget(event.target);
  };

  const getCenterDeliveryUsers = (distCenterIds) => {
    if (!distCenterIds) {
      setDeliveryManNameList([]);
      return;
    }

    const cachedUsers = deliveryUsersCacheRef.current.get(distCenterIds);
    if (cachedUsers) {
      setDeliveryManNameList(cachedUsers);
      return;
    }

    axios
      .get(`api/DistCenter/GetCenterDeliveryUsersDDL/${distCenterIds}`)
      .then(({ data }) => {
        const users = data?.response
          ? data.response.map((item) => ({
            value: item.id,
            label: item.fullname,
          }))
          : [];
        deliveryUsersCacheRef.current.set(distCenterIds, users);
        setDeliveryManNameList(users);
      })
      .catch(() => setDeliveryManNameList([]));
  };

  const loadPaymentTypes = (distributorIds) => {
    const firstDistributorId = (distributorIds || "")
      .split(",")
      .map((value) => value.trim())
      .find(Boolean);

    if (!firstDistributorId) {
      setPaymentTypeList([]);
      return;
    }

    const cachedPaymentTypes = paymentTypesCacheRef.current.get(firstDistributorId);
    if (cachedPaymentTypes) {
      setPaymentTypeList(cachedPaymentTypes);
      return;
    }

    axios
      .get(`api/SCR/NonVizLinkPaymentType?distributorId=${firstDistributorId}`)
      .then(({ data }) => {
        const items = (data?.response || []).map((item) => ({
          value: item.id,
          label: item.name?.toLowerCase() === "onebill" ? "1Bill" : item.name,
        }));
        paymentTypesCacheRef.current.set(firstDistributorId, items);
        setPaymentTypeList(items);
      })
      .catch(() => setPaymentTypeList([]));
  };

  const getData = (page) => {
    setCurrentPageNo(page);
    fetchReportData(formRequestObject({ pageNumber: page + 1 }));
  };

  const pageSizeChanged = (pageSize) => {
    setCurrentPageNo(0);
    setCurrentPageSize(pageSize);
    fetchReportData(formRequestObject({ pageNumber: 1, pageSize }));
  };

  const loadDataByFilter = () => {
    setCurrentPageNo(0);
    fetchReportData(formRequestObject({ pageNumber: 1 }));
    setShow(false);
  };

  const resetFilters = (resetForm) => {
    resetForm();
    const nextFilter = createDefaultFilter();
    setFilter(nextFilter);
    setDeliveryManNameList([]);
    setDistCenterList([]);
    setPaymentTypeList([]);
    setCurrentPageNo(0);
    fetchReportData({
      fromDate: formatDate(nextFilter.fromDate),
      toDate: formatDate(nextFilter.toDate),
      pageNumber: 1,
      pageSize: currentPageSize,
      searchText,
      distributorIds: null,
      distCenterIds: null,
      dlmIds: null,
      paymentTypeIds: null,
      shopCode: null,
      invoiceNo: null,
      scrNo: null,
    });
    setShow(false);
  };

  const openDetail = useCallback((row) => {
    const requestId = ++openDetailRequestRef.current;
    const category = row.transactionCategory || "Non-VizLink";
    const cachedDetail = peekSCRTransactionDetailCache(row.transactionId, category);

    setSelectedRow(row);
    setDetail(cachedDetail);
    setDetailLoading(!cachedDetail);

    getSCRTransactionDetail(row.transactionId, category)
      .then(({ data }) => {
        if (requestId !== openDetailRequestRef.current) {
          return;
        }
        setDetail(data?.response || null);
      })
      .catch((error) => {
        if (requestId !== openDetailRequestRef.current) {
          return;
        }
        if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
          return;
        }
        if (!cachedDetail) {
          setSelectedRow(null);
          setDetail(null);
        }
      })
      .finally(() => {
        if (requestId === openDetailRequestRef.current) {
          setDetailLoading(false);
        }
      });
  }, []);

  const prefetchDetail = useCallback((row) => {
    if (!row?.transactionId) {
      return;
    }

    prefetchSCRTransactionDetail(row.transactionId, row.transactionCategory || "Non-VizLink");
  }, []);

  const closeDetail = () => {
    setSelectedRow(null);
    setDetail(null);
    setDetailLoading(false);
  };

  const handleReopen = async () => {
    if (!selectedRow?.transactionId) {
      return false;
    }

    setReopening(true);
    try {
      const success = await props.reopenSCRTransaction(
        selectedRow.transactionId,
        selectedRow.scrId ?? detail?.scrId ?? detail?.SCRId
      );
      if (success) {
        closeDetail();
      }
      return success;
    } finally {
      setReopening(false);
    }
  };

  const showDistributorFilter =
    userRights == null ||
    userRights?.userType === "CoreAppUser" ||
    userRights?.userType === "Administrator";

  const columns = useMemo(
    () => [
      {
        Header: "SCR No",
        accessor: "scrNo",
      },
      {
        Header: "Dist Center",
        accessor: "distCenterName",
      },
      {
        Header: "Shop Code/Viz ID",
        accessor: "shopCodeVizId",
      },
      {
        Header: "DLM",
        accessor: "dlmName",
      },
      {
        Header: "Invoice No.",
        accessor: "vizproInvoiceNo",
      },
      {
        Header: "Payment Type",
        accessor: "paymentType",
        Cell: ({ value }) => (value?.toLowerCase() === "onebill" ? "1Bill" : value),
      },
      {
        Header: "Transaction Date",
        accessor: "transactionDate",
        Cell: ({ value }) =>
          value ? (
            <div style={{ width: "75px" }}>
              <Moment format="DD/MM/YYYY">{value}</Moment>
            </div>
          ) : (
            "-"
          ),
      },
      {
        Header: renderHeader("Amount", true),
        accessor: "amount",
        Cell: ({ value }) => renderCell(value, true),
      },
      {
        Header: "Status",
        accessor: "canReverse",
        Cell: ({ row }) => <ReversalStatusBadge item={row.original} />,
      },
      {
        Header: "Action",
        accessor: "transactionId",
        Cell: ({ row }) => (
          <Button
            variant="link"
            className="p-0"
            title="View transaction detail"
            onMouseEnter={() => prefetchDetail(row.original)}
            onFocus={() => prefetchDetail(row.original)}
            onClick={() => openDetail(row.original)}
          >
            <i className="fas fa-eye" />
          </Button>
        ),
      },
    ],
    [openDetail, prefetchDetail]
  );

  return (
    <div>
      <VzPageContainer title="Daily Orders > Transaction Reversal" compact>
        <Row>
          <Col md={{ span: 3 }} style={{ float: "left" }}>
            <h4>
              <b>Transaction Reversal</b>
            </h4>
          </Col>
        </Row>
        <Row>
          <Col md={10} style={{ float: "right" }}>
            <div ref={ref}>
              <Button
                onClick={openFilterModal}
                className="m-3"
                variant="outline-primary"
              >
                <i className="fa fa-filter" />
                Filters
              </Button>

              <Overlay
                show={show}
                target={target}
                placement="bottom"
                container={ref}
                containerPadding={20}
                rootClose
                onHide={() => setShow(false)}
              >
                <Popover
                  id="scr-reversal-filters"
                  style={{ maxWidth: "360px", width: "360px" }}
                >
                  <Popover.Header as="h3">Filters</Popover.Header>
                  <Popover.Body>
                    <Formik
                      enableReinitialize
                      initialValues={filter}
                      validator={() => ({})}
                      onSubmit={loadDataByFilter}
                    >
                      {(formik) => (
                        <Form autoComplete="off">
                          <Row>
                            {(showDistributorFilter) && (
                              <Col xs={12} md={12}>
                                <FormikControl
                                  formik={formik}
                                  control="react-select"
                                  label="Distributor"
                                  name="distributor"
                                  options={distributorList}
                                  isMulti
                                  isClearable
                                  isSearchable
                                  handleOnChange={(selected) => {
                                    const nextDistributor = selected || [];
                                    setFilter({
                                      ...filter,
                                      distributor: nextDistributor,
                                      distCenter: [],
                                      deliveryManName: [],
                                      paymentType: [],
                                    });
                                    formik.setFieldValue("distributor", nextDistributor);
                                    formik.setFieldValue("distCenter", []);
                                    formik.setFieldValue("deliveryManName", []);
                                    formik.setFieldValue("paymentType", []);
                                    if (!toCommaSeparated(nextDistributor)) {
                                      setDistCenterList([]);
                                      setPaymentTypeList([]);
                                    }
                                  }}
                                  value={filter.distributor}
                                />
                              </Col>
                            )}
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Distribution Center"
                                name="distCenter"
                                options={distCenterList}
                                isMulti
                                isClearable
                                isSearchable
                                handleOnChange={(selected) => {
                                  const nextCenters = selected || [];
                                  setFilter({
                                    ...filter,
                                    distCenter: nextCenters,
                                    deliveryManName: [],
                                  });
                                  formik.setFieldValue("distCenter", nextCenters);
                                  formik.setFieldValue("deliveryManName", []);
                                  getCenterDeliveryUsers(toCommaSeparated(nextCenters));
                                }}
                                value={filter.distCenter}
                              />
                            </Col>
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Deliveryman Name"
                                name="deliveryManName"
                                options={deliveryManNameList}
                                isMulti
                                isClearable
                                isSearchable
                                handleOnChange={(selected) => {
                                  setFilter({
                                    ...filter,
                                    deliveryManName: selected || [],
                                  });
                                  formik.setFieldValue("deliveryManName", selected || []);
                                }}
                                value={filter.deliveryManName}
                              />
                            </Col>
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="react-select"
                                label="Payment Type"
                                name="paymentType"
                                options={paymentTypeList}
                                isMulti
                                isClearable
                                isSearchable
                                handleOnChange={(selected) => {
                                  setFilter({
                                    ...filter,
                                    paymentType: selected || [],
                                  });
                                  formik.setFieldValue("paymentType", selected || []);
                                }}
                                value={filter.paymentType}
                              />
                            </Col>
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="input"
                                type="text"
                                label="Shop Code / Viz-ID"
                                name="shopCode"
                                handleOnChange={(value) => {
                                  setFilter({ ...filter, shopCode: value });
                                  formik.setFieldValue("shopCode", value);
                                }}
                                value={filter.shopCode}
                              />
                            </Col>
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="input"
                                type="text"
                                label="Invoice No."
                                name="invoiceNo"
                                handleOnChange={(value) => {
                                  setFilter({ ...filter, invoiceNo: value });
                                  formik.setFieldValue("invoiceNo", value);
                                }}
                                value={filter.invoiceNo}
                              />
                            </Col>
                            <Col xs={12} md={12}>
                              <FormikControl
                                formik={formik}
                                control="input"
                                type="text"
                                label="SCR No."
                                name="scrNo"
                                handleOnChange={(value) => {
                                  setFilter({ ...filter, scrNo: value });
                                  formik.setFieldValue("scrNo", value);
                                }}
                                value={filter.scrNo}
                              />
                            </Col>
                            <Row style={{ paddingRight: 0 }}>
                              <Col xs={12} md={6}>
                                <FormikControl
                                  formik={formik}
                                  type="fromDate"
                                  control="date"
                                  label="From Date"
                                  name="fromDate"
                                  handleOnChange={(value) => {
                                    const { fromDate, toDate } = normalizeDateRange(
                                      value,
                                      filter.toDate,
                                      "fromDate"
                                    );
                                    setFilter({ ...filter, fromDate, toDate });
                                    formik.setFieldValue("fromDate", fromDate);
                                    formik.setFieldValue("toDate", toDate);
                                  }}
                                  value={filter.fromDate}
                                  maxDate={getMaxFromDate(filter.toDate)}
                                  minDate={getMinFromDate(filter.toDate)}
                                />
                              </Col>
                              <Col xs={12} md={6}>
                                <FormikControl
                                  formik={formik}
                                  control="date"
                                  type="toDate"
                                  label="To Date"
                                  name="toDate"
                                  handleOnChange={(value) => {
                                    const { fromDate, toDate } = normalizeDateRange(
                                      filter.fromDate,
                                      value,
                                      "toDate"
                                    );
                                    setFilter({ ...filter, fromDate, toDate });
                                    formik.setFieldValue("fromDate", fromDate);
                                    formik.setFieldValue("toDate", toDate);
                                  }}
                                  value={filter.toDate}
                                  minDate={filter.fromDate}
                                  maxDate={getMaxSelectableToDate(filter.fromDate)}
                                />
                              </Col>
                            </Row>
                          </Row>
                          <div className="row" style={{ marginTop: 20 }}>
                            <div className="form-group">
                              <Link
                                onClick={resetFilters.bind(null, formik.resetForm)}
                                to="#"
                                className="active"
                                style={{
                                  padding: "0px",
                                  marginTop: "10px",
                                  float: "left",
                                }}
                              >
                                Reset
                              </Link>
                              <Link
                                onClick={() => {
                                  setShow(false);
                                  setTarget(null);
                                }}
                                to="#"
                                className="active"
                                style={{
                                  padding: "0px",
                                  marginTop: "10px",
                                  float: "right",
                                }}
                              >
                                Cancel
                              </Link>
                              <div className="float-right">
                                <Button
                                  type="submit"
                                  className="btn btn-primary"
                                  style={{
                                    marginRight: "10px",
                                    float: "right",
                                  }}
                                >
                                  Apply
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </Popover.Body>
                </Popover>
              </Overlay>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12}>
            <SCRV2TransactionReversalDataTable
              data={reportList.items || []}
              columns={columns}
              goToPreviousPage={getData}
              goToNextPage={getData}
              totalRecords={reportList.totalRecords || 0}
              pageSizeChange={pageSizeChanged}
              myPageSize={currentPageSize}
              pageNo={currentPageNo}
              searchChanged={searchChanged}
            />
          </Col>
        </Row>

        <SCRV2TransactionReversalDetailModal
          show={!!selectedRow}
          listItem={selectedRow}
          detail={detail}
          loading={detailLoading}
          reopening={reopening}
          onClose={closeDetail}
          onReopen={handleReopen}
        />
      </VzPageContainer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  reportList: state.SCRV2TransactionReversal?.list,
  allDistributorList: state.ddlList.allDistributorList || [],
  selectedAddJobDistCentersList: state.ddlList.selectedAddJobDistCentersList || [],
});

export default connect(mapStateToProps, {
  getSCRReversibleTransactionList,
  reopenSCRTransaction,
  getDropdownList,
  getDropdownListByPost,
})(SCRV2TransactionReversal);
